/**
 * 美編生圖 API hook。送 prompt + 來源附件 UUID,後端打 OpenAI 回 b64。
 */
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import type {
  ApiSuccess,
  GenerateDesignImageInput,
  GenerateDesignImageResult,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

export function useGenerateDesignImage() {
  return useMutation({
    mutationFn: async (
      input: GenerateDesignImageInput,
    ): Promise<GenerateDesignImageResult> => {
      try {
        const res = await apiClient.post<ApiSuccess<GenerateDesignImageResult>>(
          '/design-image/generate',
          input,
          // 後端 OpenAI 端最多 180s,client 給 200s 緩衝
          { timeout: 200_000 },
        );
        return res.data.data;
      } catch (err) {
        // 把 server 回的真實 message(例:OpenAI 的 4xx body)拋出來給 UI 顯示,
        // 否則 axios 的 default message 只有 "Request failed with status code XXX"。
        // server 走 AllExceptionsFilter,body shape 是 { error: { code, message }, meta }
        if (axios.isAxiosError(err)) {
          const data = err.response?.data as
            | { error?: { message?: string }; message?: string }
            | undefined;
          const serverMessage = data?.error?.message ?? data?.message;
          if (serverMessage) {
            throw new Error(serverMessage);
          }
        }
        throw err;
      }
    },
  });
}

/** b64 PNG → File,給既有 uploadAttachment(interviewId, file) 用。 */
export function b64ToPngFile(b64: string, filename: string): File {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: 'image/png' });
}
